import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import messages from './messages';

export type AnswerNowButtonProps = {
	onClick: () => void;
	isLoading?: boolean;
	isDisabled?: boolean;
	testId?: string;
	spacing?: React.ComponentProps<typeof Button>['spacing'];
};

/**
 * Button component that allows users to skip extended thinking and get a faster response.
 * Appears when FAST_MODE_RESPONSE_AVAILABLE trace is received during streaming.
 * Renders as a subtle (borderless) button with hover state.
 */
export const AnswerNowButton = ({
	onClick,
	isLoading = false,
	isDisabled = false,
	testId,
	spacing,
}: AnswerNowButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const handleClick = useCallback(() => {
		if (!isLoading && !isDisabled) {
			onClick();
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'answerNowButton',
				attributes: {
					is_loading: isLoading,
					is_disabled: isDisabled,
				},
			});
		}
	}, [onClick, isLoading, isDisabled, sendAnalyticsEvent]);

	return (
		<Button
			appearance="subtle"
			{...(spacing && { spacing })}
			onClick={handleClick}
			isLoading={isLoading}
			isDisabled={isDisabled}
			testId={testId ?? 'answer-now-button'}
		>
			{formatMessage(
				isLoading ? messages.answerNowButtonLoadingLabel : messages.answerNowButtonLabel,
			)}
		</Button>
	);
};
